import React, { useMemo, useState } from "react";
import {
    Avatar,
    Box,
    Menu,
    MenuItem,
    Typography,
    Divider,
    Button,
    CircularProgress,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { User as UserIcon } from "lucide-react";

import { userStorage } from "../../../utils/storage";
import { logoutStorefrontSession } from "../../../http";
import { useNavigate } from "react-router-dom";
import { TYPOGRAPHY_STYLES } from "../../../styles/typographyStyles";
import { UserClientFields } from "../../../types/user/userClient";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { AppConfigFields, ContactInfoFields } from "../../../types/appConfigEntities";
import { useUserAddresses, useUserClients } from "../../../contexts/UserDataContext";
import { useUser } from "@ecommerce/store";

const TEXT = {
    hello: { ...TYPOGRAPHY_STYLES.rubikLarge, fontWeight: 700 },
    label: {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        color: "text.secondary",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
    },
    value: { ...TYPOGRAPHY_STYLES.rubikMedium, fontWeight: 600 },
    meta: { ...TYPOGRAPHY_STYLES.rubikSmall, opacity: 0.85 },
    link: {
        ...TYPOGRAPHY_STYLES.rubikSmall,
        p: 0,
        minWidth: 0,
        textTransform: "none",
        justifyContent: "flex-start",
        alignSelf: "flex-start",
        fontWeight: 700,
        color: "primary.main",
        "&:hover": {
            backgroundColor: "transparent",
            textDecoration: "underline",
        },
    },
} as const;

const MENU_ITEM_SX = {
    px: 2,
    py: 1,
    borderRadius: 1,
    "&:hover": { backgroundColor: "action.hover" },
} as const;

const MENU_ITEM_TEXT_SX = {
    ...TYPOGRAPHY_STYLES.rubikMedium,
    fontWeight: 700,
} as const;

const MENU_ITEM_TEXT_DANGER_SX = {
    ...MENU_ITEM_TEXT_SX,
    color: "error.main",
} as const;

type UserProfileMenuProps = {
    triggerStyle?: React.CSSProperties;
};

export const UserProfileMenu = ({ triggerStyle }: UserProfileMenuProps = {}) => {
    const user = userStorage.get();
    const navigate = useNavigate();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const clearStoreUser = useUser((s) => s.clearUser);

    const enabled = !!user;

    const { active: activeAddress, loading: loadingAddresses } = useUserAddresses(enabled);
    const { active: activeClient, loading: loadingClients } = useUserClients(enabled);

    const initials = useMemo(() => {
        if (!user) return null;
        return `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase();
    }, [user]);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const goToProfileTab = (tab: "direcciones" | "clientes") => {
        handleClose();
        navigate(`/perfil?tab=${tab}`);
    };

    const handleLogout = async () => {
        try {
            await logoutStorefrontSession();
        } finally {
            clearStoreUser();
            window.location.replace("/");
        }
    };

    const addressLine1 = activeAddress
        ? `${activeAddress.codigoPostal} - ${activeAddress.calle} ${activeAddress.calleNro}${
              activeAddress.deptoPiso ? ` (${activeAddress.deptoPiso})` : ""
          }`
        : null;

    const addressLine2 = activeAddress
        ? `${activeAddress.barrio ? `${activeAddress.barrio}, ` : ""}${activeAddress.descLocalidad}, ${
              activeAddress.descProvincia
          }, ${activeAddress.descPais}`
        : null;

    const clientTitle = activeClient ? activeClient[UserClientFields.CompanyName] : null;
    const clientMeta = activeClient
        ? `${activeClient[UserClientFields.IdentityDocumentTypeDesc]} ${activeClient[UserClientFields.IdentityDocumentNumber]}`
        : null;

    const contactInfo = window.APP_CONFIG?.[AppConfigFields.ContactData];
    const whatsappPhone = (contactInfo?.[ContactInfoFields.Whatsapp] || "").replace(/\D/g, "");
    const whatsappMessage =
        contactInfo?.[ContactInfoFields.WhatsappMessage] ||
        "Hola como estas? Quiero solicitar información.";

    const whatsappHref = whatsappPhone
        ? `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(whatsappMessage)}`
        : "";

    return (
        <>
            {triggerStyle ? (
                <button
                    type="button"
                    onClick={handleOpen}
                    style={triggerStyle}
                    aria-label={user ? "Abrir menú de usuario" : "Iniciar sesión o registrarse"}
                >
                    {user ? initials : <UserIcon size={16} strokeWidth={1.6} />}
                </button>
            ) : (
                <Avatar
                    onClick={handleOpen}
                    sx={{
                        width: 36,
                        height: 36,
                        bgcolor: user ? "black" : "grey.300",
                        color: user ? "white" : "grey.700",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                    }}
                >
                    {user ? initials : <PersonIcon sx={{ fontSize: 26 }} />}
                </Avatar>
            )}

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    elevation: 3,
                    sx: { borderRadius: 2, minWidth: 320, maxWidth: 360, p: 1 },
                }}
            >
                {!user ? (
                    <Box>
                        <MenuItem
                            onClick={() => {
                                handleClose();
                                navigate("/login");
                            }}
                            sx={MENU_ITEM_SX}
                        >
                            <Typography sx={MENU_ITEM_TEXT_SX}>Iniciar sesión</Typography>
                        </MenuItem>

                        <MenuItem
                            onClick={() => {
                                handleClose();
                                navigate("/registro");
                            }}
                            sx={MENU_ITEM_SX}
                        >
                            <Typography sx={MENU_ITEM_TEXT_SX}>Registrarse</Typography>
                        </MenuItem>
                    </Box>
                ) : (
                    <Box>
                        <Box sx={{ px: 2, pt: 1, pb: 1 }}>
                            <Typography sx={TEXT.hello}>
                                Hola, {user.firstName} {user.lastName}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        {/* Cliente activo */}
                        <Box sx={{ px: 2, py: 1 }}>
                            <Typography sx={TEXT.label}>Cliente activo</Typography>

                            {loadingClients ? (
                                <Box
                                    sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}
                                >
                                    <CircularProgress size={16} />
                                    <Typography sx={TEXT.meta}>Cargando...</Typography>
                                </Box>
                            ) : activeClient ? (
                                <>
                                    <Typography
                                        sx={{ ...TEXT.value, mt: 0.5, whiteSpace: "normal" }}
                                    >
                                        {clientTitle}
                                    </Typography>
                                    <Typography
                                        sx={{ ...TEXT.meta, mt: 0.25, whiteSpace: "normal" }}
                                    >
                                        {clientMeta}
                                    </Typography>
                                </>
                            ) : (
                                <Typography sx={{ ...TEXT.meta, mt: 0.5 }}>
                                    Sin cliente seleccionado
                                </Typography>
                            )}

                            <Button
                                variant="text"
                                disableRipple
                                onClick={() => goToProfileTab("clientes")}
                                sx={{ ...TEXT.link, mt: 1 }}
                            >
                                Cambiar cliente seleccionado
                            </Button>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <Box sx={{ px: 2, py: 1 }}>
                            <Typography sx={TEXT.label}>Dirección activa</Typography>

                            {loadingAddresses ? (
                                <Box
                                    sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}
                                >
                                    <CircularProgress size={16} />
                                    <Typography sx={TEXT.meta}>Cargando...</Typography>
                                </Box>
                            ) : activeAddress ? (
                                <>
                                    <Typography
                                        sx={{ ...TEXT.value, mt: 0.5, whiteSpace: "normal" }}
                                    >
                                        {addressLine1}
                                    </Typography>
                                    <Typography
                                        sx={{ ...TEXT.meta, mt: 0.25, whiteSpace: "normal" }}
                                    >
                                        {addressLine2}
                                    </Typography>
                                </>
                            ) : (
                                <Typography sx={{ ...TEXT.meta, mt: 0.5 }}>
                                    Sin dirección seleccionada
                                </Typography>
                            )}

                            <Button
                                variant="text"
                                disableRipple
                                onClick={() => goToProfileTab("direcciones")}
                                sx={{ ...TEXT.link, mt: 1 }}
                            >
                                Cambiar dirección seleccionada
                            </Button>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <MenuItem
                            onClick={() => {
                                handleClose();
                                navigate("/perfil");
                            }}
                            sx={MENU_ITEM_SX}
                        >
                            <Typography sx={MENU_ITEM_TEXT_SX}>Perfil y configuración</Typography>
                        </MenuItem>

                        <MenuItem
                            onClick={() => {
                                handleClose();
                                if (whatsappHref)
                                    window.open(whatsappHref, "_blank", "noopener,noreferrer");
                            }}
                            sx={{
                                ...MENU_ITEM_SX,
                                display: "flex",
                                alignItems: "center",
                            }}
                            disabled={!whatsappHref}
                        >
                            <Typography sx={MENU_ITEM_TEXT_SX}>Contactanos</Typography>

                            <WhatsAppIcon
                                sx={{
                                    fontSize: 18,
                                    ml: 1,
                                    color: "#4dc247",
                                }}
                            />
                        </MenuItem>

                        <Divider sx={{ my: 1 }} />

                        <MenuItem onClick={handleLogout} sx={MENU_ITEM_SX}>
                            <Typography sx={MENU_ITEM_TEXT_DANGER_SX}>Cerrar sesión</Typography>
                        </MenuItem>
                    </Box>
                )}
            </Menu>
        </>
    );
};
